// src/screens/GST/ReconcileDashboard.js

import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Alert,
  Spinner,
  Form,
  Card,
} from "react-bootstrap";
import { selectUser } from "../../features/userSlice";
import "./GstReconcile.css";

// === Adjust these if your backend uses different routes ===
const API_PENDING_BATCHES = "/api/gst2b/getPendingBatchesForReconcile";
const API_BATCH_DETAILS = "/api/gst2b/getBatchDetails";
const API_RECONCILE_BATCH = "/api/gst2b/reconcileBatch";

const GstReconcile = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const token = user?.token;
  const emp_id = user?.emp_id;

  const [period, setPeriod] = useState("");
  const [entity, setEntity] = useState("");
  const [loading, setLoading] = useState(false);

  const [pendingBatches, setPendingBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null); // {month, entity_code}
  const [batchSummary, setBatchSummary] = useState(null);
  const [batchRecords, setBatchRecords] = useState([]);

  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loadingBatchDetails, setLoadingBatchDetails] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const [filterStatus, setFilterStatus] = useState("ALL"); // ALL / PENDING / RECONCILED / ONLY_2B
  const [searchText, setSearchText] = useState("");

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const monthOptions = [
    "JAN-2025","FEB-2025","MAR-2025","APR-2025","MAY-2025","JUN-2025",
    "JUL-2025","AUG-2025","SEP-2025","OCT-2025","NOV-2025","DEC-2025"
  ];

  const entityOptions = [
    { code: "SE", name: "Singhal Steel & Power Pvt Ltd" },
    { code: "SS", name: "Shyam ISAP Pvt Ltd" }
  ];

  // Redirect to login if not logged in
  useEffect(() => {
    if (!user || !token) {
      navigate("/login");
    }
  }, [user, token, navigate]);

  // =========================
  // Load Pending Batches
  // =========================
  const loadPendingBatches = async () => {
    if (!token) return;
    try {
      setLoadingBatches(true);
      setErrorMsg("");
      setSuccessMsg("");

      const res = await axios.post(API_PENDING_BATCHES, {
        headers: { Authorization: `Bearer ${token}` },
      });

      //setPendingBatches(res.data || []);
      setPendingBatches(res.data?.data || []);

    } catch (err) {
      console.error("Error loading pending batches:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load pending batches";
      setErrorMsg(msg);
    } finally {
      setLoadingBatches(false);
    }
  };

  useEffect(() => {
    loadPendingBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // =========================
  // Load Batch Details
  // =========================
  const handleViewBatch = async (batch) => {
    setSelectedBatch(batch);
  
    const payload = {
      batch_id: batch.BATCH_ID
    };
  
    const res = await axios.post(API_BATCH_DETAILS, payload, {
      headers: { Authorization: `Bearer ${token}` }
    });
  
    setBatchRecords(res.data?.records || []);
  };
  
  // =========================
  // Reconcile Batch
  // =========================
  /*
  const handleReconcileBatch = async () => {
    if (!selectedBatch || !token || !emp_id) return;

    setErrorMsg("");
    setSuccessMsg("");

    try {
      setReconciling(true);

      const payload = {
        month: selectedBatch.batch_month,
        entity_code: selectedBatch.entity_code,
        emp_id,
      };

      const res = await axios.post(API_RECONCILE_BATCH, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const msg =
        res?.data?.message ||
        `Reconciliation completed. Reconciled: ${res?.data?.reconciled ?? "-"}, Pending: ${res?.data?.pending ?? "-"}`;
      setSuccessMsg(msg);

      // Reload batch details & pending batch list
      await handleViewBatch(selectedBatch);
      await loadPendingBatches();
    } catch (err) {
      console.error("Error reconciling batch:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to reconcile batch";
      setErrorMsg(msg);
    } finally {
      setReconciling(false);
    }
  };
  */
  const handleReconcileBatch = async () => {
    if (!canRunReconcile || !token || !emp_id) {
      setErrorMsg("Please select GSTR2B Period and Entity before reconciliation");
      return;
    }
    
    setErrorMsg("");
    setSuccessMsg("");
  
    try {
      setReconciling(true);
      const payload = {
        period,            // 👈 selected GSTR2B period
        entity_code: entity, // 👈 selected entity
        emp_id
      };
  
      const res = await axios.post(API_RECONCILE_BATCH,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
  
      setSuccessMsg(res.data?.message || "Reconciliation done");
  
      await loadPendingBatches();
      setSelectedBatch(null);
      setBatchRecords([]);
  
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || "Reconciliation failed");
    } finally {
      setReconciling(false);
    }
  };
  
  // =========================
  // Filtered + Paginated Records
  // =========================
  const filteredRecords = useMemo(() => {
    let data = [...batchRecords];

    if (filterStatus === "PENDING") {
      data = data.filter((r) => r.matched !== "Y");
    } else if (filterStatus === "RECONCILED") {
      data = data.filter((r) => r.matched === "Y");
    } else if (filterStatus === "ONLY_2B") {
      // assuming backend marks "Only in 2B" via remarks / match_type / null ERP ID
      data = data.filter(
        (r) =>
          (!r.erp_purchase_id || r.erp_purchase_id === null) &&
          (r.match_type === null || r.match_type === "" || r.match_type === "ONLY_2B")
      );
    }

    if (searchText && searchText.trim() !== "") {
      const s = searchText.trim().toLowerCase();
      data = data.filter(
        (r) =>
          (r.gstin && r.gstin.toLowerCase().includes(s)) ||
          (r.bill_no && r.bill_no.toLowerCase().includes(s))
      );
    }

    return data;
  }, [batchRecords, filterStatus, searchText]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRecords.length / (pageSize || 20))
  );

  const paginatedRecords = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredRecords.slice(startIndex, endIndex);
  }, [filteredRecords, page, pageSize]);

  // Reset page if filter/search changes and page is out-of-range
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  // =========================
  // Helpers
  // =========================
  const getRowClass = (r) => {
    if (r.matched === "Y") {
      return "table-success";
    }
    if (
      (!r.erp_purchase_id || r.erp_purchase_id === null) &&
      (r.match_type === null || r.match_type === "" || r.match_type === "ONLY_2B")
    ) {
      return "table-danger";
    }
    if (r.match_type && r.match_type.toUpperCase().includes("PARTIAL")) {
      return "table-warning";
    }
    return "";
  };
  const canRunReconcile =
  Boolean(period) &&
  Boolean(entity) &&
  !reconciling;

  const canReconcile =
    selectedBatch &&
    !reconciling &&
    (batchSummary?.pending || 0) > 0;

  // =========================
  // RENDER
  // =========================
  return (
    <Container fluid className="mt-3">
      <Row className="mb-2">
        <Col>
          <h5>GST Reconciliation - Pending Batches</h5>
        </Col>
      </Row>
      <Row className="mb-3 small">
        <Col md={4}>
          <Form.Label>Select GSTR2B Period</Form.Label>
          <Form.Select
            value={period}
            disabled={loading}
            onChange={(e) => setPeriod(e.target.value)}
            className="small"
          >
            <option value="">-- Select Month --</option>
            {monthOptions.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </Form.Select>
        </Col>

        <Col md={4}>
          <Form.Label>Select Entity</Form.Label>
          <Form.Select
            value={entity}
            disabled={loading}
            onChange={(e) => setEntity(e.target.value)}
            className="small"
          >
            <option value="">-- Select Entity --</option>
            {entityOptions.map((e) => (
              <option key={e.code} value={e.code}>
                {e.code} - {e.name}
              </option>
            ))}
          </Form.Select>
        </Col>

      </Row>
      {(!period || !entity) && (
        <Row className="mb-2">
          <Col>
            <div className="text-muted small">
              Please select both <strong>GSTR2B Period</strong> and
              <strong> Entity</strong> to enable reconciliation.
            </div>
          </Col>
        </Row>
      )}

      {successMsg && (
        <Row className="mb-2">
          <Col>
            <Alert
              variant="success"
              onClose={() => setSuccessMsg("")}
              dismissible
            >
              {successMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {errorMsg && (
        <Row className="mb-2">
          <Col>
            <Alert
              variant="danger"
              onClose={() => setErrorMsg("")}
              dismissible
            >
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* ==================== TOP: Pending Batches ==================== */}
      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Pending Batches (Not Reconciled)</span>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="success"
                  disabled={!canRunReconcile}
                  onClick={handleReconcileBatch}
                >
                  {reconciling ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Reconciling...
                    </>
                  ) : (
                    "Reconcile"
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={loadPendingBatches}
                  disabled={loadingBatches}
                >
                  {loadingBatches ? (
                    <>
                      <Spinner animation="border" size="sm" /> Refreshing...
                    </>
                  ) : (
                    "Refresh"
                  )}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-2">
              {loadingBatches ? (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" /> Loading batches...
                </div>
              ) : pendingBatches.length === 0 ? (
                <div className="text-center py-2">
                  No pending batches found.
                </div>
              ) : (
                <Table
                  bordered
                  hover
                  size="sm"
                  className="mb-0"
                  responsive
                >
                  <thead className="table-light">
                    <tr>
                      <th rowSpan="2">#</th>
                      <th rowSpan="2">Month</th>
                      <th rowSpan="2">Batch No</th>
                      <th colSpan="3" style={{ textAlign: "center" }}>Records Count</th>
                      <th colSpan="3" style={{ textAlign: "center" }}>Taxable Value</th>
                      <th rowSpan="2" colSpan="1" style={{ textAlign: "center" }}>Action</th>
                    </tr>
                    {/* Sub Header Row */}
                    <tr>
                      {/* Under Records Count */}
                      <th style={{ textAlign: "center", width: "33.33%" }}>Reconciled</th>
                      <th style={{ textAlign: "center", width: "33.33%" }}>Pending</th>
                      <th style={{ textAlign: "center", width: "33.33%" }}>Total</th>
                      {/* Under Taxable Value */}
                      <th style={{ textAlign: "center", width: "33.33%" }}>Reconciled</th>
                      <th style={{ textAlign: "center", width: "33.33%" }}>Pending</th>
                      <th style={{ textAlign: "center", width: "33.33%" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingBatches.map((b, idx) => (
                      <tr
                        key={`${b.batch_month}-${b.entity_code}-${idx}`}
                        className={
                          selectedBatch &&
                          selectedBatch.batch_month === b.batch_month &&
                          selectedBatch.entity_code === b.entity_code
                            ? "table-info"
                            : ""
                        }
                      >
                        <td>{idx + 1}</td>
                        <td>{b.PERIOD}</td>
                        <td>{b.BATCH_ID}</td>
                        <td className="text-end">
                          {b.RECONCILED_COUNT ?? ""}
                        </td>
                        <td className="text-end">
                          {b.PENDING_COUNT ?? ""}
                        </td>
                        <td className="text-end">
                          {b.TOTAL_COUNT ?? ""}
                        </td>
                        <td className="text-end">
                          {b.RECONCILED_TAXBLE_VALUE?.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) ?? ""}
                        </td>
                        <td className="text-end">
                          {b.PENDING_TAXBLE_VALUE?.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) ?? ""}
                        </td>
                        <td className="text-end">
                          {b.TOTAL_TAXBLE_VALUE?.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) ?? ""}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleViewBatch(b)}
                          >
                            View Batch
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ==================== BOTTOM: Detail Grid ==================== */}
      {selectedBatch && (
        <Row className="mb-3">
          <Col>
            <Card>
              <Card.Header>
                <Row className="align-items-center">
                  <Col xs={12} md={4} className="mb-2 mb-md-0">
                    <strong>Batch Records</strong>
                    {loadingBatchDetails && (
                      <span className="ms-2">
                        <Spinner animation="border" size="sm" />
                      </span>
                    )}
                  </Col>
                  <Col xs={12} md={4} className="mb-2 mb-md-0">
                    <Form.Control
                      size="sm"
                      placeholder="Search by GSTIN / Bill No"
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setPage(1);
                      }}
                    />
                  </Col>
                  <Col xs={12} md={4} className="d-flex justify-content-md-end">
                    <Form.Select
                      size="sm"
                      style={{ maxWidth: "200px" }}
                      value={filterStatus}
                      onChange={(e) => {
                        setFilterStatus(e.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="ALL">All</option>
                      <option value="PENDING">Pending</option>
                      <option value="RECONCILED">Reconciled</option>
                      <option value="ONLY_2B">Only in 2B</option>
                    </Form.Select>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body className="p-2">
                {batchRecords.length === 0 ? (
                  <div className="text-center py-2">
                    No records for selected batch.
                  </div>
                ) : (
                  <>
                    <Table
                      bordered
                      hover
                      size="sm"
                      className="mb-2"
                      responsive
                    >
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>GSTIN</th>
                          <th>Bill No</th>
                          <th>Bill Date</th>
                          <th className="text-end">2B Taxable</th>
                          <th className="text-end">ERP Assessable</th>
                          <th>Match Type</th>
                          <th>Status</th>
                          <th>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRecords.map((r, idx) => (
                          <tr key={r.gstr2b_id || idx} className={getRowClass(r)}>
                            <td>{(page - 1) * pageSize + idx + 1}</td>
                            <td>{r.gstin}</td>
                            <td>{r.bill_no}</td>
                            <td>{r.bill_date}</td>
                            <td className="text-end">
                              {r.taxable_value?.toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }) ?? ""}
                            </td>
                            <td className="text-end">
                              {r.assessable_value?.toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }) ?? ""}
                            </td>
                            <td>{r.match_type}</td>
                            <td>
                              {r.matched === "Y"
                                ? "Reconciled"
                                : (!r.erp_purchase_id ||
                                    r.erp_purchase_id === null) &&
                                  (r.match_type === null ||
                                    r.match_type === "" ||
                                    r.match_type === "ONLY_2B")
                                ? "Only in 2B"
                                : "Pending"}
                            </td>
                            <td>{r.remarks}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {/* Pagination Controls */}
                    <Row className="align-items-center">
                      <Col xs={12} md={4} className="mb-2 mb-md-0">
                        <Form.Select
                          size="sm"
                          style={{ maxWidth: "120px" }}
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(parseInt(e.target.value, 10) || 20);
                            setPage(1);
                          }}
                        >
                          <option value={10}>10 / page</option>
                          <option value={20}>20 / page</option>
                          <option value={50}>50 / page</option>
                          <option value={100}>100 / page</option>
                        </Form.Select>
                      </Col>
                      <Col
                        xs={12}
                        md={8}
                        className="d-flex justify-content-md-end"
                      >
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="me-1"
                          disabled={page <= 1}
                          onClick={() => setPage(1)}
                        >
                          {"<<"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="me-1"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          {"<"}
                        </Button>
                        <span className="mx-2 small">
                          Page {page} of {totalPages}
                        </span>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="me-1"
                          disabled={page >= totalPages}
                          onClick={() =>
                            setPage((p) => Math.min(totalPages, p + 1))
                          }
                        >
                          {">"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={page >= totalPages}
                          onClick={() => setPage(totalPages)}
                        >
                          {">>"}
                        </Button>
                      </Col>
                    </Row>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default GstReconcile;
